// Libs
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  startAfter,
  DocumentSnapshot,
  QuerySnapshot,
  FirestoreError,
  or,
  and,
  QueryConstraint,
  CollectionReference,
  DocumentData,
  QueryDocumentSnapshot,
} from "firebase/firestore";

// Local
import { ExistingCustomer, CustomerManager } from "../models/customer";
import { CustomerFilter } from "../components/EmailMarketing/SmartCustomerSelector";
import { EMAIL_CAMPAIGN_DEFAULTS } from "../constants/email";

export interface CustomerQueryResult {
  customers: ExistingCustomer[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}

export interface CustomerQueryOptions {
  siteKey: string;
  filters: CustomerFilter[];
  pageSize?: number;
  lastDoc?: DocumentSnapshot | null;
  excludeCustomerIds?: string[];
  onProgress?: (fetched: number, estimated: number) => void;
  emailLimit?: number; // Maximum number of customers with emails needed
}

/**
 * Enhanced customer query function that builds optimized Firestore queries
 * based on the provided filters and fetches ALL matching results across multiple pages
 */
export async function queryCustomersWithFilters(
  options: CustomerQueryOptions,
): Promise<CustomerQueryResult> {
  const {
    siteKey,
    filters,
    pageSize = EMAIL_CAMPAIGN_DEFAULTS.PAGE_SIZE,
    lastDoc,
    excludeCustomerIds = [],
    onProgress,
    emailLimit,
  } = options;

  const db = getFirestore();
  const collectionRef = collection(db, "siteKeys", siteKey, "customers");

  // If this is a pagination call (lastDoc provided), just get the next page
  if (lastDoc) {
    return await getSinglePage(
      collectionRef,
      filters,
      pageSize,
      lastDoc,
      excludeCustomerIds,
    );
  }

  // Get estimated count for progress tracking
  let estimatedTotal = 0;
  if (onProgress) {
    try {
      estimatedTotal = await getCustomerCountWithFilters(
        siteKey,
        filters,
        excludeCustomerIds,
      );
    } catch (err) {
      console.warn("Could not get estimated count for progress tracking:", err);
    }
  }

  // Otherwise, fetch all pages
  let allCustomers: ExistingCustomer[] = [];
  let currentLastDoc: DocumentSnapshot | null = null;
  let hasMore = true;
  let customersWithEmailCount = 0;

  while (hasMore) {
    const result: CustomerQueryResult = await getSinglePage(
      collectionRef,
      filters,
      pageSize,
      currentLastDoc,
      excludeCustomerIds,
    );

    allCustomers = [...allCustomers, ...result.customers];
    currentLastDoc = result.lastDoc;
    hasMore = result.hasMore;

    // Count customers with email addresses
    if (emailLimit) {
      customersWithEmailCount = allCustomers.filter(
        (customer) => customer.email && customer.email.length > 0,
      ).length;

      // Stop fetching if we have enough customers with emails
      if (customersWithEmailCount >= emailLimit) {
        console.log(
          `Stopped fetching: Found ${customersWithEmailCount} customers with emails (limit: ${emailLimit})`,
        );
        hasMore = false;
      }
    }

    // Report progress
    if (onProgress) {
      onProgress(allCustomers.length, estimatedTotal);
    }

    // Safety check to prevent infinite loops
    if (allCustomers.length > EMAIL_CAMPAIGN_DEFAULTS.MAX_QUERY_RESULTS) {
      console.warn(
        `Query returned more than ${EMAIL_CAMPAIGN_DEFAULTS.MAX_QUERY_RESULTS} customers, stopping pagination`,
      );
      break;
    }
  }

  return {
    customers: allCustomers,
    lastDoc: currentLastDoc,
    hasMore: false, // We've fetched everything we need
  };
}

/**
 * Helper function to get a single page of results
 */
async function getSinglePage(
  collectionRef: CollectionReference<DocumentData>,
  filters: CustomerFilter[],
  pageSize: number,
  lastDoc: DocumentSnapshot | null,
  excludeCustomerIds: string[],
): Promise<CustomerQueryResult> {
  // Build the query constraints
  const constraints: QueryConstraint[] = [where("deleted", "==", false)];

  // Process each filter
  for (const filter of filters) {
    switch (filter.type) {
      case "customerType":
        constraints.push(where("type", "==", filter.value));
        break;

      case "hasEmail":
        if (filter.value === true) {
          // Has email - check that email array exists and is not empty
          constraints.push(where("email", "!=", null));
        } else {
          // No email - this is trickier in Firestore, we'll handle it in post-processing
        }
        break;

      case "excludeCampaigns":
        // This will be handled by excludeCustomerIds parameter
        break;

      case "tags":
        if (Array.isArray(filter.value) && filter.value.length > 0) {
          constraints.push(where("tags", "array-contains-any", filter.value));
        }
        break;

      case "dateRange":
        if (filter.value.startDate) {
          constraints.push(
            where("timestampCreated", ">=", filter.value.startDate),
          );
        }
        if (filter.value.endDate) {
          constraints.push(
            where("timestampCreated", "<=", filter.value.endDate),
          );
        }
        break;
    }
  }

  // Add ordering and pagination
  constraints.push(orderBy("timestampLastModified", "desc"));
  constraints.push(limit(pageSize));

  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  // Build and execute the query
  const q = query(collectionRef, ...constraints);
  const querySnapshot = await getDocs(q);

  // Process results
  let customers = querySnapshot.docs.map((doc) =>
    CustomerManager.createFromFirestoreSnapshot(doc),
  );

  // Post-process filters that can't be handled in Firestore
  customers = postProcessFilters(customers, filters, excludeCustomerIds);

  const lastDocument: DocumentSnapshot | null =
    querySnapshot.docs.length > 0
      ? (querySnapshot.docs[querySnapshot.docs.length - 1] as DocumentSnapshot)
      : null;

  const hasMore = querySnapshot.docs.length === pageSize;

  return {
    customers,
    lastDoc: lastDocument,
    hasMore,
  };
}

/**
 * Get count of customers matching specific filters
 */
export async function getCustomerCountWithFilters(
  siteKey: string,
  filters: CustomerFilter[],
  excludeCustomerIds: string[] = [],
): Promise<number> {
  // For counts, we'll fetch all matching customers and count them
  // In a production system, you might want to use Firestore count() queries
  // or maintain separate count documents

  const result: CustomerQueryResult = await queryCustomersWithFilters({
    siteKey,
    filters,
    pageSize: EMAIL_CAMPAIGN_DEFAULTS.PAGE_SIZE, // Larger page size for counting
    excludeCustomerIds,
  });

  let totalCount = result.customers.length;
  let hasMore = result.hasMore;
  let lastDoc = result.lastDoc;

  // Continue fetching if there are more results
  while (hasMore && lastDoc) {
    const nextResult: CustomerQueryResult = await queryCustomersWithFilters({
      siteKey,
      filters,
      pageSize: EMAIL_CAMPAIGN_DEFAULTS.PAGE_SIZE,
      lastDoc,
      excludeCustomerIds,
    });

    totalCount += nextResult.customers.length;
    hasMore = nextResult.hasMore;
    lastDoc = nextResult.lastDoc;
  }

  return totalCount;
}

/**
 * Get customer IDs from specific campaigns for exclusion
 */
export async function getCustomerIdsFromCampaigns(
  siteKey: string,
  campaignIds: string[],
): Promise<string[]> {
  if (campaignIds.length === 0) return [];

  const db = getFirestore();
  const customerIds = new Set<string>();

  // Fetch each campaign and extract customer IDs
  for (const campaignId of campaignIds) {
    try {
      const campaignRef = collection(db, "siteKeys", siteKey, "emailCampaigns");
      const campaignQuery = query(
        campaignRef,
        where("__name__", "==", campaignId),
      );
      const campaignSnapshot = await getDocs(campaignQuery);

      campaignSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.recipients && Array.isArray(data.recipients)) {
          data.recipients.forEach((recipient: any) => {
            if (recipient.customerID) {
              customerIds.add(recipient.customerID);
            }
          });
        }
      });
    } catch (error) {
      console.error(`Error fetching campaign ${campaignId}:`, error);
    }
  }

  return Array.from(customerIds);
}

/**
 * Subscribe to real-time customer updates with filters
 */
export function subscribeToCustomersWithFilters(
  options: CustomerQueryOptions & {
    onChange: (result: CustomerQueryResult) => void;
    onError?: (error: FirestoreError) => void;
  },
): () => void {
  const {
    siteKey,
    filters,
    pageSize = 100,
    lastDoc,
    excludeCustomerIds = [],
    onChange,
    onError,
  } = options;

  const db = getFirestore();
  const collectionRef = collection(db, "siteKeys", siteKey, "customers");

  // Build the query constraints
  const constraints: QueryConstraint[] = [where("deleted", "==", false)];

  // Process filters
  for (const filter of filters) {
    switch (filter.type) {
      case "customerType":
        constraints.push(where("type", "==", filter.value));
        break;

      case "hasEmail":
        if (filter.value === true) {
          constraints.push(where("email", "!=", null));
        }
        break;
    }
  }

  constraints.push(orderBy("timestampLastModified", "desc"));
  constraints.push(limit(pageSize));

  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  const q = query(collectionRef, ...constraints);

  return onSnapshot(
    q,
    (querySnapshot) => {
      let customers = querySnapshot.docs.map((doc) =>
        CustomerManager.createFromFirestoreSnapshot(doc),
      );

      // Post-process filters
      customers = postProcessFilters(customers, filters, excludeCustomerIds);

      const lastDocument =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      const hasMore = querySnapshot.docs.length === pageSize;

      onChange({
        customers,
        lastDoc: lastDocument,
        hasMore,
      });
    },
    onError,
  );
}

/**
 * Post-process filters that can't be handled efficiently in Firestore
 */
function postProcessFilters(
  customers: ExistingCustomer[],
  filters: CustomerFilter[],
  excludeCustomerIds: string[],
): ExistingCustomer[] {
  let filteredCustomers = customers;

  // Apply exclusions
  if (excludeCustomerIds.length > 0) {
    filteredCustomers = filteredCustomers.filter(
      (customer) => !excludeCustomerIds.includes(customer.id),
    );
  }

  // Apply filters that need post-processing
  for (const filter of filters) {
    switch (filter.type) {
      case "hasEmail":
        if (filter.value === false) {
          // Filter for customers without email
          filteredCustomers = filteredCustomers.filter(
            (customer) => !customer.email || customer.email.length === 0,
          );
        } else {
          // Filter for customers with email (additional check)
          filteredCustomers = filteredCustomers.filter(
            (customer) => customer.email && customer.email.length > 0,
          );
        }
        break;
    }
  }

  return filteredCustomers;
}

/**
 * Get available filter options with counts
 */
export async function getAvailableFilterOptions(siteKey: string): Promise<{
  customerTypes: { value: string; count: number }[];
  hasEmailCounts: { hasEmail: number; noEmail: number };
  totalCustomers: number;
}> {
  const db = getFirestore();
  const collectionRef = collection(db, "siteKeys", siteKey, "customers");

  // Get all customers (you might want to paginate this for very large datasets)
  const q = query(
    collectionRef,
    where("deleted", "==", false),
    limit(10000), // Adjust based on your needs
  );

  const querySnapshot = await getDocs(q);
  const customers = querySnapshot.docs.map((doc) =>
    CustomerManager.createFromFirestoreSnapshot(doc),
  );

  // Count by customer type
  const typeCounts = customers.reduce(
    (acc, customer) => {
      const type = customer.type || "unknown";
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  // Count email availability
  const hasEmailCount = customers.filter(
    (customer) => customer.email && customer.email.length > 0,
  ).length;
  const noEmailCount = customers.length - hasEmailCount;

  return {
    customerTypes: Object.entries(typeCounts).map(([value, count]) => ({
      value,
      count,
    })),
    hasEmailCounts: {
      hasEmail: hasEmailCount,
      noEmail: noEmailCount,
    },
    totalCustomers: customers.length,
  };
}
